<script setup lang="ts">
import { computed } from 'vue';

const props = withDefaults(defineProps<{
  percent: number;
  size?: number;
  strokeWidth?: number;
}>(), {
  size: 44,
  strokeWidth: 3,
});

const radius = computed(() => (props.size / 2) - (props.strokeWidth / 2));
const center = computed(() => props.size / 2);

const progressColor = computed(() => {
  if (props.percent >= 100) return '#10b981';
  return '#0052CC';
});

// Use circle elements instead of path for cleaner rendering
const circumference = computed(() => 2 * Math.PI * radius.value);

// Calculate stroke-dasharray and stroke-dashoffset for progress
const strokeDasharray = computed(() => circumference.value);
const strokeDashoffset = computed(() => {
  const progress = Math.max(0, Math.min(100, props.percent));
  return circumference.value * (1 - progress / 100);
});
</script>

<template>
  <div
    class="course-card-progress-ring"
    :style="{ width: `${size}px`, height: `${size}px` }"
    :title="`${percent}% complete`"
  >
    <svg :viewBox="`0 0 ${size} ${size}`" class="circular-progress" role="img" :aria-label="`${percent}% complete`">
      <!-- Background circle -->
      <circle
        class="circle-bg"
        :cx="center"
        :cy="center"
        :r="radius"
        fill="none"
        stroke="#ECECEC"
        :stroke-width="strokeWidth"
      />
      <!-- Progress circle (only render if percent > 0) -->
      <circle
        v-if="percent > 0"
        class="circle-progress"
        :cx="center"
        :cy="center"
        :r="radius"
        fill="none"
        :stroke="progressColor"
        :stroke-width="strokeWidth"
        stroke-linecap="round"
        :stroke-dasharray="strokeDasharray"
        :stroke-dashoffset="strokeDashoffset"
        :transform="`rotate(-90 ${center} ${center})`"
      />
    </svg>
    <span class="progress-text">{{ percent }}%</span>
  </div>
</template>
